import React, { useState, useEffect, useRef } from 'react';
import { Search, Download, Calendar, ArrowLeft } from 'lucide-react';
import {
    LineChart, Line,
    BarChart, Bar,
    PieChart, Pie, Cell,
    XAxis, YAxis, CartesianGrid,
    Tooltip as RechartsTooltip,
    Legend, ResponsiveContainer
} from 'recharts';
import { getClientUsageReport } from '../../../services/admin/api';
import './clientUsageReport.css';
import { toast } from 'react-toastify';
import { useParams, useNavigate } from 'react-router-dom';
import 'react-toastify/dist/ReactToastify.css';
import axiosInstance from '../../../services/axiosInstance';

const ClientUsageReport = () => {
    const navigate = useNavigate();
    const { clientId } = useParams();
    const [reportData, setReportData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');

    // Active chart view tab: 'line' | 'bar' | 'pie'
    const [activeChartType, setActiveChartType] = useState('line');

    // Sorting states
    const [sortColumn, setSortColumn] = useState('clientName');
    const [sortOrder, setSortOrder] = useState('asc');

    const companyName = "";
    const isInitialMount = useRef(true);

    const loadReport = async (
        customStart = startDate,
        customEnd = endDate,
        customSearch = searchTerm
    ) => {
        if ((customStart && !customEnd) || (!customStart && customEnd)) {
            toast.error("Please select both From Date and To Date, or leave blank to view all records.");
            return;
        }

        if (customStart && customEnd && new Date(customStart) > new Date(customEnd)) {
            toast.error("From Date cannot be later than To Date");
            return;
        }

        try {
            setLoading(true);
            const data = await getClientUsageReport(
                customStart || null,
                customEnd || null,
                customSearch,
                clientId,
                companyName
            );

            setReportData(data || []);
        } catch (error) {
            console.error("Error loading usage report:", error);
            setReportData([]);
        } finally {
            setLoading(false);
        }
    };

    // 1. Initial Load: Fetches ALL data from DB on mount
    useEffect(() => {
        loadReport('', '', searchTerm);
    }, [clientId]);

    // 2. Debounced search filter across records
    useEffect(() => {
        if (isInitialMount.current) {
            isInitialMount.current = false;
            return;
        }

        const delayDebounceFn = setTimeout(() => {
            loadReport(startDate, endDate, searchTerm);
        }, 400);

        return () => clearTimeout(delayDebounceFn);
    }, [searchTerm]);

    
    const handleApplyFilters = (e) => {
    e.preventDefault();

    if (!startDate && !endDate) {
        toast.error("Please select a date before applying the filter.");
        return;
    }

    if (!startDate || !endDate) {
        toast.error("Please select both From Date and To Date.");
        return;
    }

    if (new Date(startDate) > new Date(endDate)) {
        toast.error("From Date cannot be later than To Date.");
        return;
    }

    loadReport(startDate, endDate, searchTerm);
};


    const handleResetFilters = () => {
        setStartDate('');
        setEndDate('');
        setSearchTerm('');
        loadReport('', '', ''); // Reloads all DB data
    };

    const handleSort = (column) => {
        if (sortColumn === column) {
            setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
        } else {
            setSortColumn(column);
            setSortOrder('asc');
        }
    };

    const sortedData = [...reportData].sort((a, b) => {
        let valA = a[sortColumn];
        let valB = b[sortColumn];

        if (valA == null) valA = '';
        if (valB == null) valB = '';

        if (sortColumn === 'registeredDate') {
            return sortOrder === 'asc'
                ? new Date(valA || 0) - new Date(valB || 0)
                : new Date(valB || 0) - new Date(valA || 0);
        }

        if (typeof valA === 'string') {
            return sortOrder === 'asc'
                ? valA.localeCompare(valB)
                : valB.localeCompare(valA);
        } else {
            return sortOrder === 'asc'
                ? valA - valB
                : valB - valA;
        }
    });

    // Determine whether we are viewing a single client or multiple clients
    const isSingleClient = sortedData.length === 1;

    // Single-client: Map the 3 metrics across the X-axis so a connecting line is always drawn
    const singleClientData = isSingleClient ? [
        { stage: 'Total Candidates', count: sortedData[0].totalCandidates || 0, color: '#4D0054' },
        { stage: 'Pending Cases', count: sortedData[0].pendingCases || 0, color: '#ea580c' },
        { stage: 'Completed Cases', count: sortedData[0].completedCases || 0, color: '#16a34a' }
    ] : [];

    // Multi-client: Compare clients side-by-side
    const multiClientData = !isSingleClient ? sortedData.slice(0, 15).map(item => ({
        name: item.clientName && item.clientName !== 'N/A' ? item.clientName : `Client #${item.clientId}`,
        totalCandidates: item.totalCandidates || 0,
        completedCases: item.completedCases || 0,
        pendingCases: item.pendingCases || 0,
    })) : [];

    // KPI Aggregations
    const totalActiveClients = reportData.length;
    const totalCandidatesRegistered = reportData.reduce((acc, curr) => acc + (curr.totalCandidates || 0), 0);
    const overallCompletedCases = reportData.reduce((acc, curr) => acc + (curr.completedCases || 0), 0);
    const overallPendingCases = reportData.reduce((acc, curr) => acc + (curr.pendingCases || 0), 0);
    const avgCompletionRate = totalActiveClients > 0
        ? Math.round(reportData.reduce((acc, curr) => acc + (curr.completionPercentage || 0), 0) / totalActiveClients)
        : 0;

    const pieData = [
        { name: 'Completed Cases', value: overallCompletedCases, color: '#16a34a' },
        { name: 'Pending Cases', value: overallPendingCases, color: '#ea580c' },
    ];

    const exportToExcel = async () => {
         if (!reportData || reportData.length === 0) {
        toast.warning("No candidate data available to export.");
        return;
              }
        try {
            toast.info("Generating Excel document...");
            const response = await axiosInstance.get('/admin/users/client-reports/export/excel', {
                params: {
                    fromDate: startDate || null,
                    toDate: endDate || null,
                    search: searchTerm,
                    clientId: clientId,
                    companyName: companyName
                },
                responseType: 'blob'
            });
            const namePrefix = clientId && reportData.length > 0 && reportData[0].companyName
                ? `${reportData[0].companyName.replace(/\s+/g, '_').toLowerCase()}_client`
                : "all_clients";
            const dateSuffix = startDate && endDate ? `${startDate}_to_${endDate}` : "all_time";
            const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
            const link = document.createElement('a');
            link.href = window.URL.createObjectURL(blob);
            link.download = `${namePrefix}_usage_report_${dateSuffix}.xlsx`;
            link.click();
            toast.success("Excel exported successfully!");
        } catch (error) {
            console.error("Excel download failed:", error);
            toast.error("Failed to download Excel file.");
        }
    };

    const exportToPdf = async () => {
        if (!reportData || reportData.length === 0) {
        toast.warning("No candidate data available to export.");
        return;
    }
        try {
            toast.info("Generating PDF document...");
            const response = await axiosInstance.get('/admin/users/client-reports/export/pdf', {
                params: {
                    fromDate: startDate || null,
                    toDate: endDate || null,
                    search: searchTerm,
                    clientId: clientId,
                    companyName: companyName
                },
                responseType: 'blob'
            });
            const namePrefix = clientId && reportData.length > 0 && reportData[0].companyName
                ? `${reportData[0].companyName.replace(/\s+/g, '_').toLowerCase()}_client`
                : "all_clients";
            const dateSuffix = startDate && endDate ? `${startDate}_to_${endDate}` : "all_time";
            const blob = new Blob([response.data], { type: 'application/pdf' });
            const link = document.createElement('a');
            link.href = window.URL.createObjectURL(blob);
            link.download = `${namePrefix}_usage_report_${dateSuffix}.pdf`;
            link.click();
            toast.success("PDF exported successfully!");
        } catch (error) {
            console.error("PDF download failed:", error);
            toast.error("Failed to download PDF file.");
        }
    };

    const renderSortArrow = (columnName) => {
        if (sortColumn !== columnName) return <span className="sort-arrows"> ▲▼</span>;
        return <span className="sort-arrows active">{sortOrder === 'asc' ? ' ▲' : ' ▼'}</span>;
    };

    const getCompletionBadgeClass = (percentage) => {
        if (percentage >= 70) return 'badge-green';
        if (percentage >= 30) return 'badge-yellow';
        return 'badge-red';
    };

    return (
        <div className="client-wrapper">
            <button
                type="button"
                onClick={() => navigate(-1)}
                className="client-usage-back-button"
            >
                <ArrowLeft size={18} />
                <span>Back</span>
            </button>

            {/* Date Filters Ribbon Form */}
            <form onSubmit={handleApplyFilters} className="report-filter-ribbon">
                <div className="filter-group">
                    <label><Calendar size={16} /> From Date</label>
                    <input
                        type="date"
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                    />
                </div>

                <div className="filter-group">
                    <label><Calendar size={16} /> To Date</label>
                    <input
                        type="date"
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                    />
                </div>

                <div className="filter-actions">
                    <button type="submit" className="candidate-btn btn-apply">Apply</button>
                    <button type="button" className="candidate-btn btn-reset" onClick={handleResetFilters}>Reset / All Data</button>
                </div>

                <div className="export-btn-group">
                    <button type="button" className="export-excel-btn" onClick={exportToExcel}>
                        <Download size={16} /> Export Excel
                    </button>
                    <button type="button" className="export-pdf-btn" onClick={exportToPdf}>
                        <Download size={16} /> Export PDF
                    </button>
                </div>
            </form>

            {/* Executive Summary KPI Cards */}
            <div className="kpi-cards-grid">
                <div className="kpi-card">
                    <h4>Total Active Clients</h4>
                    <p>{totalActiveClients}</p>
                </div>
                <div className="kpi-card">
                    <h4>Total Candidates Registered</h4>
                    <p>{totalCandidatesRegistered}</p>
                </div>
                <div className="kpi-card">
                    <h4>Overall Completed Cases</h4>
                    <p>{overallCompletedCases}</p>
                </div>
                <div className="kpi-card">
                    <h4>Overall Pending Cases</h4>
                    <p>{overallPendingCases}</p>
                </div>
                <div className="kpi-card">
                    <h4>Avg Completion Rate</h4>
                    <p>{avgCompletionRate}%</p>
                </div>
            </div>

            {/* Interactive Visual Analytics Card (Line, Bar & Pie) */}
            {sortedData.length > 0 && (
                <div style={{
                    background: '#ffffff',
                    borderRadius: '12px',
                    padding: '20px 24px',
                    margin: '20px 0',
                    border: '1px solid #e5e7eb',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)'
                }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '10px' }}>
                        <div>
                            <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '700', color: '#1f2937' }}>
                                {isSingleClient
                                    ? `Case Progression Curve: ${sortedData[0].clientName || `Client #${sortedData[0].clientId}`}`
                                    : 'Visual Usage & Performance Analytics'}
                            </h3>
                            <p style={{ margin: '4px 0 0', fontSize: '13px', color: '#6b7280' }}>
                                {isSingleClient
                                    ? 'Connected line showing progression from Total Candidates to Pending & Completed cases'
                                    : 'Comparative overview across clients'}
                            </p>
                        </div>

                        {/* Chart Switcher Buttons */}
                        <div style={{ display: 'flex', gap: '6px', background: '#f3f4f6', padding: '4px', borderRadius: '8px' }}>
                            <button
                                type="button"
                                onClick={() => setActiveChartType('line')}
                                style={{
                                    border: 'none',
                                    padding: '6px 14px',
                                    borderRadius: '6px',
                                    cursor: 'pointer',
                                    fontSize: '13px',
                                    fontWeight: '600',
                                    background: activeChartType === 'line' ? '#4D0054' : 'transparent',
                                    color: activeChartType === 'line' ? '#fff' : '#4b5563',
                                    transition: 'all 0.2s'
                                }}
                            >
                                📈 Line Graph
                            </button>
                            <button
                                type="button"
                                onClick={() => setActiveChartType('bar')}
                                style={{
                                    border: 'none',
                                    padding: '6px 14px',
                                    borderRadius: '6px',
                                    cursor: 'pointer',
                                    fontSize: '13px',
                                    fontWeight: '600',
                                    background: activeChartType === 'bar' ? '#4D0054' : 'transparent',
                                    color: activeChartType === 'bar' ? '#fff' : '#4b5563',
                                    transition: 'all 0.2s'
                                }}
                            >
                                📊 Bar Graph
                            </button>
                            <button
                                type="button"
                                onClick={() => setActiveChartType('pie')}
                                style={{
                                    border: 'none',
                                    padding: '6px 14px',
                                    borderRadius: '6px',
                                    cursor: 'pointer',
                                    fontSize: '13px',
                                    fontWeight: '600',
                                    background: activeChartType === 'pie' ? '#4D0054' : 'transparent',
                                    color: activeChartType === 'pie' ? '#fff' : '#4b5563',
                                    transition: 'all 0.2s'
                                }}
                            >
                                🍩 Pie Chart
                            </button>
                        </div>
                    </div>

                    {/* Chart Render Area */}
                    <div style={{ width: '100%', height: 300 }}>
                        <ResponsiveContainer width="100%" height="100%">
                            {activeChartType === 'line' ? (
                                isSingleClient ? (
                                    /* Single Client Line Graph: Continuous line across the 3 stages */
                                    <LineChart data={singleClientData} margin={{ top: 15, right: 30, left: 10, bottom: 20 }}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                                        <XAxis dataKey="stage" tick={{ fontSize: 12, fill: '#374151', fontWeight: 600 }} />
                                        <YAxis tick={{ fontSize: 11, fill: '#4b5563' }} allowDecimals={false} />
                                        <RechartsTooltip />
                                        <Legend verticalAlign="top" height={36} />
                                        <Line
                                            type="monotone"
                                            dataKey="count"
                                            name={sortedData[0].clientName || `Client #${sortedData[0].clientId}`}
                                            stroke="#4D0054"
                                            strokeWidth={3}
                                            dot={{ r: 6, fill: '#4D0054', stroke: '#ffffff', strokeWidth: 2 }}
                                            activeDot={{ r: 8 }}
                                        />
                                    </LineChart>
                                ) : (
                                    /* Multi-Client Line Graph */
                                    <LineChart data={multiClientData} margin={{ top: 10, right: 20, left: -10, bottom: 25 }}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                                        <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#4b5563' }} angle={-25} textAnchor="end" interval={0} />
                                        <YAxis tick={{ fontSize: 11, fill: '#4b5563' }} allowDecimals={false} />
                                        <RechartsTooltip />
                                        <Legend verticalAlign="top" height={36} />
                                        <Line type="monotone" dataKey="totalCandidates" name="Total Candidates" stroke="#4D0054" strokeWidth={2.5} dot={{ r: 4, fill: '#4D0054' }} activeDot={{ r: 6 }} />
                                        <Line type="monotone" dataKey="completedCases" name="Completed Cases" stroke="#16a34a" strokeWidth={2} dot={{ r: 4, fill: '#16a34a' }} activeDot={{ r: 6 }} />
                                        <Line type="monotone" dataKey="pendingCases" name="Pending Cases" stroke="#ea580c" strokeWidth={2} dot={{ r: 4, fill: '#ea580c' }} activeDot={{ r: 6 }} />
                                    </LineChart>
                                )
                            ) : activeChartType === 'bar' ? (
                                isSingleClient ? (
                                    /* Single Client Bar Graph: 3 bars for Total, Pending, Completed */
                                    <BarChart data={singleClientData} margin={{ top: 15, right: 30, left: 10, bottom: 20 }}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                                        <XAxis dataKey="stage" tick={{ fontSize: 12, fill: '#374151', fontWeight: 600 }} />
                                        <YAxis tick={{ fontSize: 11, fill: '#4b5563' }} allowDecimals={false} />
                                        <RechartsTooltip />
                                        <Bar dataKey="count" name="Case Count" radius={[6, 6, 0, 0]}>
                                            {singleClientData.map((entry, index) => (
                                                <Cell key={`cell-${index}`} fill={entry.color} />
                                            ))}
                                        </Bar>
                                    </BarChart>
                                ) : (
                                    /* Multi-Client Bar Graph */
                                    <BarChart data={multiClientData} margin={{ top: 10, right: 20, left: -10, bottom: 25 }}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                                        <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#4b5563' }} angle={-25} textAnchor="end" interval={0} />
                                        <YAxis tick={{ fontSize: 11, fill: '#4b5563' }} allowDecimals={false} />
                                        <RechartsTooltip />
                                        <Legend verticalAlign="top" height={36} />
                                        <Bar dataKey="totalCandidates" name="Total Candidates" fill="#4D0054" radius={[4, 4, 0, 0]} />
                                        <Bar dataKey="completedCases" name="Completed Cases" fill="#16a34a" radius={[4, 4, 0, 0]} />
                                    </BarChart>
                                )
                            ) : (
                                /* Pie Chart */
                                <PieChart>
                                    <RechartsTooltip />
                                    <Legend verticalAlign="top" height={36} />
                                    <Pie data={pieData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={100} innerRadius={45} label>
                                        {pieData.map((entry, index) => (
                                            <Cell key={`cell-${index}`} fill={entry.color} />
                                        ))}
                                    </Pie>
                                </PieChart>
                            )}
                        </ResponsiveContainer>
                    </div>
                </div>
            )}

            {/* Search Box */}
            <div className="client-search-box" style={{ margin: "20px 0" }}>
                <div className="client-search-input-wrapper">
                    <input
                        type="text"
                        placeholder="Search Client or Company Name..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                    <Search className="client-search-icon" size={18} />
                </div>
            </div>

            {/* Data Table View Container with Registered Date */}
            <div className="clinet-table-container">
                <table className="candidates-clinet-table modern-usage-table">
                    <thead>
                        <tr>
                            <th onClick={() => handleSort('clientId')} className="sortable-th">Client ID {renderSortArrow('clientId')}</th>
                            <th onClick={() => handleSort('clientName')} className="sortable-th">Client Name {renderSortArrow('clientName')}</th>
                            <th onClick={() => handleSort('companyName')} className="sortable-th">Company Name {renderSortArrow('companyName')}</th>
                            <th onClick={() => handleSort('registeredDate')} className="sortable-th text-center">Registered Date {renderSortArrow('registeredDate')}</th>
                            <th onClick={() => handleSort('totalCandidates')} className="sortable-th text-center">Total Candidates {renderSortArrow('totalCandidates')}</th>
                            <th onClick={() => handleSort('completedCases')} className="sortable-th text-center">Completed {renderSortArrow('completedCases')}</th>
                            <th onClick={() => handleSort('pendingCases')} className="sortable-th text-center">Pending {renderSortArrow('pendingCases')}</th>
                            <th onClick={() => handleSort('totalUsageCases')} className="sortable-th text-center">Usage Count {renderSortArrow('totalUsageCases')}</th>
                            <th onClick={() => handleSort('completionPercentage')} className="sortable-th text-center">Completion % {renderSortArrow('completionPercentage')}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr>
                                <td colSpan="9" className="no-data">Loading Client Usage Performance...</td>
                            </tr>
                        ) : sortedData.length > 0 ? (
                            sortedData.map((row) => {
                                const compPct = row.completionPercentage != null && !isNaN(row.completionPercentage)
                                    ? Math.round(row.completionPercentage)
                                    : 0;
                                const regDate = row?.registeredDate
                                    ? String(row.registeredDate).split('T')[0]
                                    : 'N/A';

                                return (
                                    <tr key={row.clientId}>
                                        <td className="text-center">{row?.clientId ?? 'N/A'}</td>
                                        <td className="text-left">{row?.clientName || "N/A"}</td>
                                        <td className="text-left">{row?.companyName || "N/A"}</td>
                                        <td className="text-center">{regDate}</td>
                                        <td className="text-center">{row?.totalCandidates ?? 0}</td>
                                        <td className="text-center">{row?.completedCases ?? 0}</td>
                                        <td className="text-center">{row?.pendingCases ?? 0}</td>
                                        <td className="text-center">{row?.totalUsageCases ?? 0}</td>
                                        <td className="text-center">
                                            <span className={`completion-badge ${getCompletionBadgeClass(compPct)}`}>
                                                {compPct}%
                                            </span>
                                        </td>
                                    </tr>
                                );
                            })
                        ) : (
                            <tr>
                                <td colSpan="9" className="no-data">No records found.</td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default  ClientUsageReport;